import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useProperties } from '@/hooks/useProperties';
import { useCreateTask } from '@/hooks/admin/AdminTodosTabs/useTasks';
import {
  useCreateTaskTag,
  useTaskTags,
} from '@/hooks/admin/AdminTodosTabs/useTasks';
import { useUsers } from '@/hooks/useUsers';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import CustomDatePicker from '@/components/CustomDatePicker';
import { format, parse } from 'date-fns';
import { translateRoleToSpanish } from '@/utils/shared/translateRoleToSpanish';
import { useAuth } from '@/contexts/AuthContext';
import { Role } from '@/constants/rolePermissions';

interface TaskFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultDueDate?: Date | null;
  onSubmit?: () => void;
  initialAssignedUserId?: string | null;
}

interface NewTagForm {
  name: string;
  color: string;
}

const defaultColors = [
  '#10B981',
  '#3B82F6',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#EC4899',
  '#6366F1',
];

const TaskFormModal = ({
  open,
  onOpenChange,
  defaultDueDate,
  onSubmit,
  initialAssignedUserId = null,
}: TaskFormModalProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(() => {
    if (defaultDueDate) {
      return format(defaultDueDate, 'yyyy-MM-dd');
    }
    return '';
  });

  const [assignedUserId, setAssignedUserId] = useState(() => {
    if (initialAssignedUserId) {
      return initialAssignedUserId;
    }
    return '__none__';
  });

  const [propertyId, setPropertyId] = useState('__none__');
  const [tagId, setTagId] = useState('__none__');
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [newTag, setNewTag] = useState<NewTagForm>({
    name: '',
    color: defaultColors[0],
  });
  const [propertyOpen, setPropertyOpen] = useState(false);

  const createTask = useCreateTask();
  const createTag = useCreateTaskTag();

  const { data: tags = [], error: tagsError } = useTaskTags();
  const { data: properties = [], error: propertiesError } = useProperties();

  const { profile } = useAuth();

  const userRoles = useMemo<Role[]>(() => {
    if (!profile) return [];

    switch (profile.role) {
      case 'super_admin':
        return ['cleaning', 'property_manager'];
      case 'property_manager':
      case 'cleaning':
        return ['cleaning'];
      default:
        return [];
    }
  }, [profile]);

  const { data: users = [], error: usersError } = useUsers(userRoles);

  const availableUsers = useMemo(() => {
    return users.filter((u) => userRoles.includes(u.role));
  }, [users, userRoles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      alert('El título es obligatorio.');
      return;
    }

    const taskData = {
      title: title.trim(),
      description: description.trim() || undefined,
      due_date: dueDate || undefined,
      assigned_user_id:
        assignedUserId === '__none__' ? undefined : assignedUserId,
      property_id: propertyId === '__none__' ? undefined : propertyId,
      tag_id: tagId === '__none__' ? undefined : tagId,
      status: 'pending' as const,
      sort_order: 0,
      created_by_id: '',
    };

    try {
      createTask.mutate(taskData, {
        onSuccess: (result) => {
          console.log('TaskFormModal: Tarea creada exitosamente', result);
          resetForm();
          onOpenChange(false);

          if (onSubmit) {
            onSubmit();
          }
        },
        onError: (error) => {
          console.error('TaskFormModal: Error al crear tarea', error);
          alert('Ocurrió un error al crear la tarea. Intenta nuevamente.');
        },
      });
    } catch (error) {
      console.error('TaskFormModal: Error en handleSubmit', error);
      alert('Ocurrió un error inesperado.');
    }
  };

  const handleCreateTag = async () => {
    if (!newTag.name.trim()) return;

    createTag.mutate(newTag, {
      onSuccess: (createdTag) => {
        setTagId(createdTag.id);
        setShowNewTagForm(false);
        setNewTag({ name: '', color: defaultColors[0] });
      },
      onError: (error) => {
        console.error('Error al crear la etiqueta', error);
        alert('Ocurrió un error al crear la etiqueta.');
      },
    });
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setDueDate('');
    setAssignedUserId('__none__');
    setPropertyId('__none__');
    setTagId('__none__');
    setShowNewTagForm(false);
    setNewTag({ name: '', color: defaultColors[0] });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  useEffect(() => {
    if (open && defaultDueDate) {
      setDueDate(format(defaultDueDate, 'yyyy-MM-dd'));
    }
  }, [defaultDueDate, open]);

  const parsedDate = dueDate
    ? parse(dueDate, 'yyyy-MM-dd', new Date())
    : undefined;

  if (tagsError || usersError || propertiesError) {
    alert('Hubo un problema al cargar los datos necesarios.');
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Nueva Tarea</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título de la tarea"
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción de la tarea (opcional)"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="dueDate">Fecha de vencimiento</Label>
            <CustomDatePicker
              selected={parsedDate}
              onSelect={(date) =>
                setDueDate(date ? format(date, 'yyyy-MM-dd') : '')
              }
              placeholder="Selecciona una fecha"
              isAdminContext={true}
              className="flex-1 h-10"
            />
          </div>

          <div>
            <Label htmlFor="assignedUser">Usuario asignado</Label>
            <Select value={assignedUserId} onValueChange={setAssignedUserId}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar usuario (opcional)" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="__none__">Sin asignar</SelectItem>
                {availableUsers.map((user) => (
                  <SelectItem
                    key={user.user_id}
                    value={user.user_id || `user-${user.id}`}
                  >
                    {user.first_name} {user.last_name}
                    {' - '}
                    <span className="text-muted-foreground text-xs">
                      {translateRoleToSpanish(user.role)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="property">Propiedad</Label>
            <Popover open={propertyOpen} onOpenChange={setPropertyOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={propertyOpen}
                  className="w-full justify-between"
                >
                  {propertyId !== '__none__' && propertyId !== '__external__'
                    ? properties.find((property) => property.id === propertyId)
                        ?.shortName || 'Seleccionar propiedad...'
                    : propertyId === '__external__'
                    ? 'Propiedad externa'
                    : 'Seleccionar propiedad (opcional)'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>

              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Buscar propiedad..." />

                  <CommandList>
                    <CommandEmpty>No se encontraron propiedades.</CommandEmpty>

                    <CommandGroup>
                      <CommandItem
                        value="__none__"
                        onSelect={() => {
                          setPropertyId('__none__');
                          setPropertyOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            propertyId === '__none__'
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        Sin propiedad asignada
                      </CommandItem>

                      <CommandItem
                        value="__external__"
                        onSelect={() => {
                          setPropertyId('__external__');
                          setPropertyOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            propertyId === '__external__'
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        Propiedad externa
                      </CommandItem>

                      {properties.map((property) => (
                        <CommandItem
                          key={property.id}
                          value={property.shortName || property.title}
                          onSelect={() => {
                            setPropertyId(property.id);
                            setPropertyOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              propertyId === property.id
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                          />
                          {property.shortName || property.title}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="tag">Etiqueta</Label>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowNewTagForm(true)}
                className="text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                Nueva etiqueta
              </Button>
            </div>

            <Select value={tagId} onValueChange={setTagId}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar etiqueta (opcional)" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="__none__">Sin etiqueta</SelectItem>

                {tags
                  .filter((tag) => tag.id)
                  .map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            {showNewTagForm && (
              <Card className="mt-3">
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="newTagName">Nombre de la etiqueta</Label>

                      <Input
                        id="newTagName"
                        value={newTag.name}
                        onChange={(e) =>
                          setNewTag((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Nombre de la etiqueta"
                      />
                    </div>

                    <div>
                      <Label>Color</Label>

                      <div className="flex gap-2 mt-2">
                        {defaultColors.map((color) => (
                          <button
                            key={color}
                            type="button"
                            className={`w-6 h-6 rounded-full border-2 ${
                              newTag.color === color
                                ? 'border-gray-900'
                                : 'border-gray-300'
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() =>
                              setNewTag((prev) => ({ ...prev, color }))
                            }
                          />
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleCreateTag}
                        disabled={createTag.isPending || !newTag.name.trim()}
                      >
                        Crear etiqueta
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowNewTagForm(false)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              disabled={createTask.isPending || !title.trim()}
              className="bg-brand-primary hover:bg-brand-primary-hover"
            >
              {createTask.isPending ? 'Creando...' : 'Crear Tarea'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TaskFormModal;
